import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Sparkles, Loader2, Copy, Check, Mic2, User, BookOpen, Radio,
  PlayCircle, StopCircle, Gift, CloudSun, TrafficCone, Newspaper,
  CalendarHeart, Share2, ArrowLeft, Volume2, Globe2
} from "lucide-react";
import PageHeader from "@/components/PageHeader";
import PromptSuggestionButton from "@/components/PromptSuggestionButton";
import AiWriteScriptModal, { AiWriteButton } from "@/components/AiWriteScriptModal";
import VoiceRecorderModal from "@/components/VoiceRecorderModal";
import { SendToPrompterButton } from "@/components/SendToPrompterButton";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { api } from "@/lib/api";
import { toast } from "sonner";

// Icon + colour map per generator type returned by /api/dj/types
const TYPE_META = {
  dj_break:           { icon: Mic2,          accent: "amber",   blurb: "Punchy reads between songs — short / medium / long versions." },
  artist_intro:       { icon: User,          accent: "violet",  blurb: "3 lengths of artist intro ready for live air." },
  artist_facts:       { icon: BookOpen,      accent: "violet",  blurb: "Bio, 5 facts, timeline, latest releases & awards." },
  station_id:         { icon: Radio,         accent: "amber",   blurb: "Station ID, sweeper, liner & positioning statement." },
  show_intro:         { icon: PlayCircle,    accent: "emerald", blurb: "15s / 30s / 60s opens for your show." },
  show_outro:         { icon: StopCircle,    accent: "emerald", blurb: "Closing, thank-you, tomorrow tease & social CTA." },
  contest:            { icon: Gift,          accent: "rose",    blurb: "Call-in, text-to-win and online giveaway promos." },
  weather_break:      { icon: CloudSun,      accent: "sky",     blurb: "15s / 30s breaks + social post with live placeholders." },
  traffic_break:      { icon: TrafficCone,   accent: "orange",  blurb: "Traffic update script & social alert with placeholders." },
  entertainment_news: { icon: Newspaper,     accent: "violet",  blurb: "Radio script, web article, podcast & social variants." },
  world_news:         { icon: Globe2,        accent: "sky",     blurb: "Authoritative global newsroom segment — headline, 30s read, 90s deep dive, web article & social post." },
  event_promotion:    { icon: CalendarHeart, accent: "rose",    blurb: "6 ready promos: 30s / 60s / web / FB / IG / X." },
  social_content:     { icon: Share2,        accent: "amber",   blurb: "Platform-tailored posts: FB / IG / X / Threads / TikTok / LI / WA." },
};

const ACCENT_RING = {
  amber:   "ring-brand-amber/30 text-brand-amber",
  violet:  "ring-violet-500/30 text-violet-400",
  emerald: "ring-emerald-500/30 text-emerald-400",
  rose:    "ring-rose-500/30 text-rose-400",
  sky:     "ring-sky-500/30 text-sky-400",
  orange:  "ring-orange-500/30 text-orange-400",
};

// Generator types that produce broadcast-ready spoken scripts (voice TTS makes sense).
// Excludes pure fact sheets and social-only content.
const VOICE_READY_TYPES = new Set([
  "dj_break", "artist_intro", "station_id", "show_intro", "show_outro",
  "contest", "weather_break", "traffic_break", "entertainment_news", "world_news", "event_promotion",
]);

const FIELD_LABELS = {
  artist_name: "Artist name",
  song_title: "Song title",
  station_name: "Station name",
  station_tagline: "Station tagline",
  show_name: "Show name",
  host_name: "Host / DJ name",
  description: "Description",
  contest_name: "Contest name",
  prize: "Prize",
  rules: "Rules",
  city: "City",
  country: "Country",
  location: "Location",
  topic: "Topic / story",
  region: "Region / continent",
  angle: "Angle",
  event_name: "Event name",
  date: "Date",
  details: "Details",
};

const LONG_FIELDS = new Set(["description", "rules", "details", "angle", "topic"]);

// Map DJ subtype → best-fit script_type default for the AI Writer modal.
const DJ_SUBTYPE_TO_SCRIPT = {
  dj_break:           "sweeper",
  station_id:         "sweeper",
  show_intro:         "show",
  show_outro:         "show",
  artist_intro:       "show",
  artist_facts:       "show",
  weather_break:      "weather",
  traffic_break:      "traffic",
  entertainment_news: "news",
  world_news:         "news",
  contest:            "promo",
  event_promotion:    "promo",
  social_content:     "custom",
};

// Placeholder hints for tricky/long fields, shown as Textarea placeholders
const FIELD_PLACEHOLDERS = {
  topic: "e.g. EU summit on climate financing for emerging economies",
  region: "e.g. Europe · Middle East · Sub-Saharan Africa · Asia-Pacific",
  angle: "e.g. impact on listener's wallet, what changes tomorrow, the human cost",
};

function CopyBtn({ text, testid }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      onClick={() => {
        navigator.clipboard.writeText(text || "");
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      }}
      data-testid={testid}
      className="inline-flex items-center gap-1 rounded-md border border-line bg-bg-elevated px-2 py-1 text-xs text-ink-secondary hover:text-cyan-300 transition"
    >
      {copied ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
      {copied ? "Copied" : "Copy"}
    </button>
  );
}

function SendToImagingBtn({ text, testid, onSend }) {
  return (
    <button
      onClick={() => onSend(text)}
      data-testid={testid}
      className="inline-flex items-center gap-1 rounded-md border border-brand-amber/50 bg-brand-amber/10 px-2 py-1 text-xs text-brand-amber hover:bg-brand-amber/20 transition"
      title="Send this script to Imaging Studio → Voice tab"
    >
      <Volume2 className="h-3 w-3" />
      Send to Imaging
    </button>
  );
}

// Fields where an AI suggestion is actually useful. Skip very mechanical
// fields like dates and rules where the user has the real info.
const SUGGESTIBLE_FIELDS = new Set([
  "topic", "angle", "region", "description", "details",
  "song_title", "artist_name", "contest_name", "prize",
  "event_name", "station_tagline", "show_name",
]);

function splitVariants(text) {
  if (!text) return [];
  const chunks = text.split(/\n?\s*---\s*\n?/).map((s) => s.trim()).filter(Boolean);
  return chunks.length > 0 ? chunks : [text];
}

// Strip a leading ALL-CAPS label line like "SHORT (8-12s read)" so the script
// arrives clean in the Imaging Studio voice tab.
function cleanScript(text) {
  if (!text) return "";
  const lines = text.split(/\r?\n/);
  if (lines.length > 1) {
    const head = lines[0].trim();
    // ALL-CAPS label, optionally followed by a parenthesised note (which may contain lowercase).
    const isLabel = head.length <= 80 && /^[A-Z0-9][A-Z0-9 \-/&,.]*(\s*\([^)]*\))?\s*:?\s*$/.test(head);
    if (isLabel) {
      // Drop the label and any immediately-following blank line.
      let i = 1;
      while (i < lines.length && lines[i].trim() === "") i += 1;
      return lines.slice(i).join("\n").trim();
    }
  }
  return text.trim();
}

export default function AIDJAssistant() {
  const navigate = useNavigate();
  const [types, setTypes] = useState([]);
  const [loadingTypes, setLoadingTypes] = useState(true);
  const [selected, setSelected] = useState(null); // { id, label, fields }
  const [fields, setFields] = useState({});
  const [generating, setGenerating] = useState(false);
  const [output, setOutput] = useState("");
  const [aiOpen, setAiOpen] = useState(false);
  const [recordFor, setRecordFor] = useState(null);   // script text being recorded

  const saveRecordedTake = async (file) => {
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("label", `${selected?.label || "DJ script"} — my take`);
      fd.append("kind", "voice");
      fd.append("source", "user_recorded");
      await api.post("/imaging/assets/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Your take is saved — find it in Imaging Studio (library & Sequencer)");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save the recording");
    }
  };

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const { data } = await api.get("/dj/types");
        if (mounted) setTypes(data || []);
      } catch (e) {
        toast.error("Failed to load AI DJ Assistant tools");
      } finally {
        if (mounted) setLoadingTypes(false);
      }
    })();
    return () => { mounted = false; };
  }, []);

  const variants = useMemo(() => splitVariants(output), [output]);
  const voiceReady = selected ? VOICE_READY_TYPES.has(selected.id) : false;

  const sendToImaging = (rawText) => {
    const clean = cleanScript(rawText);
    if (!clean) {
      toast.error("Nothing to send");
      return;
    }
    sessionStorage.setItem("imaging_script", clean);
    toast.success("Sent to Imaging Studio · Voice tab");
    navigate("/dashboard/imaging?tab=voice");
  };

  const openGenerator = (t) => {
    setSelected(t);
    setFields(Object.fromEntries((t.fields || []).map((f) => [f, ""])));
    setOutput("");
  };

  const closeGenerator = () => {
    setSelected(null);
    setFields({});
    setOutput("");
    setGenerating(false);
  };

  const handleGenerate = async () => {
    if (!selected) return;
    setGenerating(true);
    setOutput("");
    try {
      const { data } = await api.post("/dj/generate", {
        type: selected.id,
        fields,
      });
      setOutput(data.output || "");
      toast.success(`${selected.label} generated`);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Generation failed";
      toast.error(msg);
    } finally {
      setGenerating(false);
    }
  };

  // === Detail view ===
  if (selected) {
    const Meta = TYPE_META[selected.id] || { icon: Sparkles, accent: "amber", blurb: "" };
    const Icon = Meta.icon;
    return (
      <div className="space-y-6" data-testid="ai-dj-detail">
        <button
          onClick={closeGenerator}
          className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted hover:text-cyan-300 transition"
          data-testid="ai-dj-back"
        >
          <ArrowLeft className="h-3 w-3" />
          Back to AI DJ Assistant
        </button>

        <PageHeader
          eyebrow="AI DJ Assistant"
          title={selected.label}
          description={Meta.blurb}
          testid="ai-dj-detail-header"
        />

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
          {/* Inputs */}
          <div className="lg:col-span-2 rounded-xl border border-line bg-bg-surface p-5">
            <div className="flex items-center justify-between gap-2 mb-4">
              <div className="flex items-center gap-2">
                <div className={`h-9 w-9 rounded-lg bg-bg-elevated grid place-items-center ring-1 ${ACCENT_RING[Meta.accent]}`}>
                  <Icon className="h-4 w-4" />
                </div>
                <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted">Inputs</div>
              </div>
              <AiWriteButton onClick={() => setAiOpen(true)} testid="ai-dj-ai-write" label="Write for me" />
            </div>
            <div className="space-y-4">
              {(selected.fields || []).map((f) => (
                <div key={f} className="space-y-1.5">
                  <div className="flex items-center justify-between gap-2">
                    <Label htmlFor={`field-${f}`} className="text-xs text-ink-secondary">
                      {FIELD_LABELS[f] || f.replace(/_/g, " ")}
                    </Label>
                    {SUGGESTIBLE_FIELDS.has(f) && (
                      <PromptSuggestionButton
                        surface="dj"
                        subtype={selected.id}
                        fieldName={f}
                        context={fields}
                        onSelect={(val) => setFields((s) => ({ ...s, [f]: val }))}
                        testid={`ai-dj-suggest-${f}`}
                      />
                    )}
                  </div>
                  {LONG_FIELDS.has(f) ? (
                    <Textarea
                      id={`field-${f}`}
                      data-testid={`ai-dj-field-${f}`}
                      value={fields[f] || ""}
                      onChange={(e) => setFields((s) => ({ ...s, [f]: e.target.value }))}
                      rows={3}
                      placeholder={FIELD_PLACEHOLDERS[f] || `Enter ${FIELD_LABELS[f] || f}`}
                    />
                  ) : (
                    <Input
                      id={`field-${f}`}
                      data-testid={`ai-dj-field-${f}`}
                      value={fields[f] || ""}
                      onChange={(e) => setFields((s) => ({ ...s, [f]: e.target.value }))}
                      placeholder={FIELD_PLACEHOLDERS[f] || `Enter ${FIELD_LABELS[f] || f}`}
                    />
                  )}
                </div>
              ))}
              <button
                onClick={handleGenerate}
                disabled={generating}
                data-testid="ai-dj-generate-btn"
                className="mt-2 inline-flex w-full items-center justify-center gap-2 rounded-md bg-brand-amber px-4 py-2.5 text-sm font-semibold text-black hover:bg-brand-amber/90 disabled:opacity-50 transition"
              >
                {generating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
                {generating ? "Generating…" : `Generate ${selected.label}`}
              </button>
            </div>
          </div>

          {/* Output */}
          <div className="lg:col-span-3 space-y-4">
            {!output && !generating && (
              <div className="rounded-xl border border-dashed border-line bg-bg-surface/40 p-10 text-center" data-testid="ai-dj-empty">
                <Sparkles className="mx-auto mb-3 h-6 w-6 text-ink-muted" />
                <div className="font-display text-lg text-ink-primary">Ready when you are.</div>
                <div className="mt-1 text-sm text-ink-muted">
                  Fill the inputs on the left, then hit Generate.
                </div>
              </div>
            )}

            {generating && (
              <div className="rounded-xl border border-line bg-bg-surface p-10 text-center" data-testid="ai-dj-loading">
                <Loader2 className="mx-auto mb-3 h-6 w-6 animate-spin text-brand-amber" />
                <div className="text-sm text-ink-secondary">Writing your {selected.label.toLowerCase()}…</div>
              </div>
            )}

            {!generating && variants.length > 0 && (
              <>
                <div className="flex items-center justify-between">
                  <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-brand-amber">
                    {variants.length} version{variants.length > 1 ? "s" : ""}
                  </div>
                  <div className="flex items-center gap-2">
                    {voiceReady && variants.length === 1 && (
                      <SendToImagingBtn text={variants[0]} testid="ai-dj-send-imaging-all" onSend={sendToImaging} />
                    )}
                    <CopyBtn text={output} testid="ai-dj-copy-all" />
                  </div>
                </div>
                {variants.map((chunk, idx) => (
                  <div key={idx} className="rounded-xl border border-line bg-bg-surface p-5" data-testid={`ai-dj-output-${idx}`}>
                    <div className="mb-2 flex items-center justify-between gap-2">
                      <span className="font-mono text-[10px] uppercase tracking-wider text-ink-muted">
                        Version {idx + 1}
                      </span>
                      <div className="flex items-center gap-2">
                        {voiceReady && (
                          <>
                            <SendToImagingBtn text={chunk} testid={`ai-dj-send-imaging-${idx}`} onSend={sendToImaging} />
                            <button
                              onClick={() => setRecordFor(chunk)}
                              data-testid={`ai-dj-record-${idx}`}
                              title="Read this script into your mic — the take is saved to your Imaging library"
                              className="inline-flex items-center gap-1 rounded-md border border-rose-500/50 bg-rose-500/10 px-2 py-1 text-xs text-rose-300 hover:bg-rose-500/20 transition"
                            >
                              <Mic2 className="h-3 w-3" />
                              Record it
                            </button>
                          </>
                        )}
                        <SendToPrompterButton
                          title={`${selected?.label || "DJ script"} — v${idx + 1}`}
                          text={chunk}
                          source="ai_dj_assistant"
                          testid={`ai-dj-send-prompter-${idx}`}
                        />
                        <CopyBtn text={chunk} testid={`ai-dj-copy-${idx}`} />
                      </div>
                    </div>
                    <pre className="whitespace-pre-wrap font-sans text-sm leading-relaxed text-ink-primary">
                      {chunk}
                    </pre>
                  </div>
                ))}
              </>
            )}
          </div>
        </div>

        {recordFor !== null && (
          <VoiceRecorderModal
            title="Record your take"
            onCancel={() => setRecordFor(null)}
            onUse={(file) => { setRecordFor(null); saveRecordedTake(file); }}
          />
        )}

        <AiWriteScriptModal
          open={aiOpen}
          onClose={() => setAiOpen(false)}
          defaultScriptType={DJ_SUBTYPE_TO_SCRIPT[selected.id] || "custom"}
          defaultTone="conversational"
          defaultLengthSec={selected.id === "world_news" || selected.id === "entertainment_news" ? 60 : 30}
          initialTopic={(selected.fields || [])
            .filter((f) => fields[f])
            .map((f) => `${FIELD_LABELS[f] || f}: ${fields[f]}`)
            .join("\n")}
          hideTitle
          onApply={({ script }) => {
            // Fill the FIRST long-form field on this generator (topic /
            // description / details / rules / angle). If none exist, use
            // whatever field is first in the fields array.
            const target = (selected.fields || []).find((f) => LONG_FIELDS.has(f))
                       || (selected.fields || [])[0];
            if (!target) { toast.info("No text field to fill on this generator."); setAiOpen(false); return; }
            setFields((s) => ({ ...s, [target]: script }));
            setAiOpen(false);
            toast.success(`Draft dropped into "${FIELD_LABELS[target] || target}". Tweak, then Generate.`);
          }}
        />
      </div>
    );
  }

  // === Grid view ===
  return (
    <div data-testid="ai-dj-assistant">
      <PageHeader
        eyebrow="AI DJ Assistant"
        title="One assistant. Thirteen generators."
        description="Pre-air prep, between-song breaks, station IDs, contests, weather, traffic, world news, social — all generated in your voice."
        testid="ai-dj-header"
      />

      {loadingTypes ? (
        <div className="rounded-xl border border-line bg-bg-surface p-10 text-center" data-testid="ai-dj-loading-types">
          <Loader2 className="mx-auto mb-3 h-6 w-6 animate-spin text-brand-amber" />
          <div className="text-sm text-ink-secondary">Loading your toolkit…</div>
        </div>
      ) : types.length === 0 ? (
        <div className="rounded-xl border border-line bg-bg-surface p-10 text-center text-sm text-ink-secondary">
          No generators available.
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="ai-dj-grid">
          {types.map((t) => {
            const Meta = TYPE_META[t.id] || { icon: Sparkles, accent: "amber", blurb: "" };
            const Icon = Meta.icon;
            return (
              <button
                key={t.id}
                onClick={() => openGenerator(t)}
                data-testid={`ai-dj-card-${t.id}`}
                className="group flex flex-col items-start gap-4 rounded-xl border border-line bg-bg-surface p-5 text-left transition hover:border-brand-amber/50 hover:bg-bg-elevated"
              >
                <div className={`grid h-11 w-11 place-items-center rounded-lg bg-bg-elevated ring-1 ${ACCENT_RING[Meta.accent]}`}>
                  <Icon className="h-5 w-5" />
                </div>
                <div className="space-y-1">
                  <div className="font-display text-lg font-bold text-ink-primary group-hover:text-cyan-300 transition">
                    {t.label}
                  </div>
                  <div className="text-sm text-ink-secondary leading-relaxed">
                    {Meta.blurb}
                  </div>
                </div>
                <div className="mt-auto font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted group-hover:text-cyan-300 transition">
                  Open generator →
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
